import React, { useState, useEffect } from 'react';
import { getPatientProviders, getProviders, assignProvider, removeProviderAssignment } from '../services/api';

interface PatientProviderAssignmentProps {
  patientId: string;
}

interface Provider {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  provider_type: string;
  specialization: string;
}

interface Assignment {
  id: string;
  provider_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  provider_type: string;
  is_primary: boolean;
  assignment_role: string;
}

const PatientProviderAssignment: React.FC<PatientProviderAssignmentProps> = ({ patientId }) => {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [availableProviders, setAvailableProviders] = useState<Provider[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [assignmentRole, setAssignmentRole] = useState('');

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      const [assignmentData, providerData] = await Promise.all([
        getPatientProviders(patientId),
        getProviders()
      ]);
      setAssignments(assignmentData);
      setAvailableProviders(providerData);
    } catch (error) {
      console.error('Error loading provider data:', error);
    }
  };

  const handleAssign = async () => {
    if (!selectedProvider) return;
    try {
      await assignProvider(patientId, {
        provider_id: selectedProvider,
        is_primary: isPrimary,
        assignment_role: assignmentRole
      });
      setShowAddForm(false);
      setSelectedProvider('');
      setIsPrimary(false);
      setAssignmentRole('');
      loadData();
    } catch (error) {
      console.error('Error assigning provider:', error);
      alert('Failed to assign provider');
    }
  };

  const handleRemove = async (providerId: string) => {
    if (!window.confirm('Remove this provider from the care team?')) return;
    try {
      await removeProviderAssignment(patientId, providerId);
      loadData();
    } catch (error) {
      console.error('Error removing provider:', error);
    }
  };

  const assignedProviderIds = new Set(assignments.map(a => a.provider_id));
  const unassignedProviders = availableProviders.filter(p => !assignedProviderIds.has(p.id));

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold text-gray-800">Care Team</h3>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-sm"
        >
          + Assign Provider
        </button>
      </div>

      {showAddForm && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Provider
              </label>
              <select
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                <option value="">-- Choose Provider --</option>
                {unassignedProviders.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.first_name} {p.last_name} ({p.provider_type})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <input
                type="text"
                value={assignmentRole}
                onChange={(e) => setAssignmentRole(e.target.value)}
                placeholder="e.g., Primary Nurse, Attending Physician"
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPrimary"
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="isPrimary" className="text-sm text-gray-700">
                Set as Primary Provider (receives all alerts)
              </label>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowAddForm(false)}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={!selectedProvider}
                className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {assignments.length === 0 ? (
          <p className="text-gray-500 text-center py-4">
            No providers assigned. Facility-wide staff will receive alerts.
          </p>
        ) : (
          assignments.map(assignment => (
            <div
              key={assignment.id}
              className="flex justify-between items-center border border-gray-200 rounded-lg p-3"
            >
              <div>
                <div className="font-medium text-gray-900">
                  {assignment.first_name} {assignment.last_name}
                  {assignment.is_primary && (
                    <span className="ml-2 px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      PRIMARY
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  {assignment.assignment_role || assignment.provider_type}
                </div>
                <div className="text-xs text-gray-500">
                  {assignment.email} {assignment.phone && `• ${assignment.phone}`}
                </div>
              </div>
              <button
                onClick={() => handleRemove(assignment.provider_id)}
                className="text-red-600 hover:text-red-800 text-sm"
              >
                Remove
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PatientProviderAssignment;
